import { useEffect, useState } from 'react';
import { getPhotoUrl } from '../services/photoService';
import { PhotoAnnotationModal } from './PhotoAnnotationModal';
import type { ProjectPhotoView } from '../types';

interface PhotoLightboxProps {
  photo: ProjectPhotoView;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onDescriptionChange?: (description: string) => void;
}

export function PhotoLightbox({
  photo,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  onDescriptionChange,
}: PhotoLightboxProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnnotationModalOpen, setIsAnnotationModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadImage = async () => {
      setIsLoading(true);
      try {
        const url = await getPhotoUrl(photo.filePath);
        if (!cancelled) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('Error loading photo:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [photo.filePath]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasPrevious && onPrevious) onPrevious();
          break;
        case 'ArrowRight':
          if (hasNext && onNext) onNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString('en-CA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90"
      onClick={onClose}
    >
      {/* Header - fixed height */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 text-white z-10">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{photo.fileName}</p>
          <p className="text-sm text-white/70">{formatDate(photo.takenAt)}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="ml-4 p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Image Container - absolute positioned with insets for header/footer */}
      <div 
        className="absolute top-16 bottom-24 left-0 right-0 flex items-center justify-center px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous Button */}
        {hasPrevious && onPrevious && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className="absolute left-4 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
            aria-label="Previous photo"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Image */}
        {isLoading ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin h-12 w-12 text-white/50"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={photo.fileName}
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: '100%', maxWidth: '100%' }}
          />
        ) : (
          <div className="text-white/50">Failed to load image</div>
        )}

        {/* Next Button */}
        {hasNext && onNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
            aria-label="Next photo"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Photo Details Footer - fixed at bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 bg-black/50 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-3xl mx-auto space-y-3">
          {/* Description */}
          {(photo.description || onDescriptionChange) && (
            <div className="flex items-start gap-3">
              {photo.description ? (
                <p className="flex-1 text-sm text-white/90 italic">
                  "{photo.description}"
                </p>
              ) : (
                <p className="flex-1 text-sm text-white/50 italic">
                  No notes added
                </p>
              )}
              {onDescriptionChange && (
                <button
                  onClick={() => setIsAnnotationModalOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  {photo.description ? 'Edit Notes' : 'Add Notes'}
                </button>
              )}
            </div>
          )}

          {/* Photo metadata */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            {photo.cameraMake && (
              <div>
                <p className="text-white/50">Camera</p>
                <p>{photo.cameraMake} {photo.cameraModel}</p>
              </div>
            )}
            {photo.hasCoordinates && (
              <div>
                <p className="text-white/50">Location</p>
                <p className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                  GPS Available
                </p>
              </div>
            )}
            {photo.fileSizeBytes && (
              <div>
                <p className="text-white/50">Size</p>
                <p>{(photo.fileSizeBytes / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            )}
            {photo.uploadedByName && (
              <div>
                <p className="text-white/50">Uploaded by</p>
                <p>{photo.uploadedByName}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Annotation Modal */}
      {onDescriptionChange && (
        <PhotoAnnotationModal
          isOpen={isAnnotationModalOpen}
          onClose={() => setIsAnnotationModalOpen(false)}
          onSave={(description) => {
            onDescriptionChange(description);
            setIsAnnotationModalOpen(false);
          }}
          initialValue={photo.description || ''}
          photoName={photo.fileName}
        />
      )}
    </div>
  );
}